import { Box, Chip, Divider, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { nanoid } from '@reduxjs/toolkit';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { representationSchemeType } from 'components/ErrorsCorrections/ErrorsTable/constants';
import { ReturnItemTableDialog } from './ReturnItemTableDialog';
import { TOrderReturnsItemProps } from '../types';
import { Duties } from '../../OrderSplitsCard/OrderDetailsRowChild/Duties';
import { Total } from '../../OrderSplitsCard/OrderDetailsRowChild/Total';

export const OrderReturnsItem: FC<TOrderReturnsItemProps> = ({
	orderReturnsItem,
	returnedOrderBreakdown,
	paymentCurrency,
}) => {
	const [detalization, setDetalization] = useState(false);
	const [openReturnDialog, setOpenReturnDialog] = useState(false);

	return (
		<Box>
			<Box
				sx={{
					border: '1px solid #00000020',
					borderRadius: '12px',
					padding: '10px',
					marginBottom: '10px',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						mb: 2,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: 1,
						}}
					>
						<Typography variant="body2" color="text.secondary">
							{orderReturnsItem?.timestamp &&
								DateTime.fromISO(orderReturnsItem.timestamp).toFormat('yyyy-MM-dd, HH:mm')}
						</Typography>
						{orderReturnsItem.status && (
							<Chip
								label={orderReturnsItem.status.toLowerCase().replaceAll('_', ' ')}
								color="primary"
								size="small"
								sx={{ p: '0 5px', ml: 1, fontSize: '.7rem', height: '20px' }}
							/>
						)}
					</Box>

					<Typography
						variant="body2"
						color="primary"
						sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}
						onClick={() => {
							// setDetalization(true);
							setOpenReturnDialog(true);
						}}
					>
						Show Split Item Table
					</Typography>
				</Box>

				{returnedOrderBreakdown.map((child) => (
					<Box sx={{ ml: 2, mb: 1 }} key={nanoid()}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'space-between',
								gap: 2,
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									gap: 1,
								}}
							>
								<Typography
									variant="body2"
									sx={{
										fontWeight: 'bold',
									}}
									color="primary"
								>
									{representationSchemeType?.[
										child.rep_scheme as keyof typeof representationSchemeType
									] ?? child.rep_scheme}
								</Typography>
								<Typography variant="body2">
									<span style={{ fontWeight: 'bold' }}>{child.id_provided_by_em} — </span>
									{child.short_description}
								</Typography>
								<Chip
									label={`${String(child.return_item_quantity)} returned`}
									size="small"
									sx={{
										p: '0 2px',
										fontSize: '.7rem',
										height: '20px',
										bgcolor: '#FFD6A7',
									}}
								/>
							</Box>

							{detalization ? (
								<Box
									sx={{
										display: 'flex',
										gap: 1,
									}}
								>
									<Typography
										variant="body2"
										sx={{
											fontSize: '.8rem',
										}}
										color="text.secondary"
									>
										<AmountAndCurrency
											amount={child.return_item_amount / child.return_item_quantity}
											currency={paymentCurrency}
											bold="bold"
											size={0.8}
											color="text.secondary"
										/>
										&nbsp;&times;&nbsp;{child.return_item_quantity}&nbsp;=&nbsp;
									</Typography>
									<AmountAndCurrency
										amount={child.return_item_amount}
										currency={paymentCurrency}
										bold="bold"
										size={0.9}
										color="text.secondary"
									/>
								</Box>
							) : (
								<AmountAndCurrency
									amount={
										+(child.return_item_amount ?? 0) +
										+(child.return_vat_value ?? 0) +
										+(child.return_eas_fee ?? 0) +
										+(child.return_vat_on_eas_fee ?? 0) +
										+(child.return_delivery_cost ?? 0) +
										+(child.return_vat_on_delivery_charge ?? 0)
									}
									currency={paymentCurrency}
									bold="bold"
									size={1}
								/>
							)}
						</Box>
						{detalization && (
							<>
								<Duties
									duty={child.return_vat_value}
									nameOfDuty="Item VAT"
									paymentCurrency={paymentCurrency}
								/>
								{!!child.return_delivery_cost && (
									<Duties
										duty={child.return_delivery_cost}
										nameOfDuty="Delivery Charge"
										paymentCurrency={paymentCurrency}
									/>
								)}
								{!!child.return_vat_on_delivery_charge && (
									<Duties
										duty={child.return_vat_on_delivery_charge}
										nameOfDuty="VAT on Delivery Charge"
										paymentCurrency={paymentCurrency}
									/>
								)}
								{!!child.return_eas_fee && (
									<Duties
										duty={child.return_eas_fee}
										nameOfDuty="EAS Fee"
										paymentCurrency={paymentCurrency}
									/>
								)}
								{!!child.return_vat_on_eas_fee && (
									<Duties
										duty={child.return_vat_on_eas_fee}
										nameOfDuty="VAT on EAS Fee (22%)"
										paymentCurrency={paymentCurrency}
									/>
								)}

								<Divider sx={{ mt: 0.4, mb: 0.4, ml: 4 }} />
								<Total
									totalPerItem={child.return_item_amount ?? 0}
									quantity={child.return_item_quantity}
									totalCost={
										+(child.return_item_amount ?? 0) +
										+(child.return_vat_value ?? 0) +
										+(child.return_eas_fee ?? 0) +
										+(child.return_vat_on_eas_fee ?? 0) +
										+(child.return_delivery_cost ?? 0) +
										+(child.return_vat_on_delivery_charge ?? 0)
									}
									paymentCurrency={paymentCurrency}
								/>
							</>
						)}
					</Box>
				))}
				{returnedOrderBreakdown.length > 1 && (
					<>
						<Divider sx={{ mt: 1, mb: 1, ml: 4 }} />

						<Box sx={{ display: 'flex', justifyContent: 'end' }}>
							<AmountAndCurrency
								amount={orderReturnsItem.total_return_amount}
								currency={paymentCurrency}
								bold="bold"
								size={1}
							/>
						</Box>
					</>
				)}
				<ReturnItemTableDialog
					open={openReturnDialog}
					onClose={() => setOpenReturnDialog(false)}
					title={`Return Items – ${orderReturnsItem.external_order_id ?? orderReturnsItem.id}`}
					items={returnedOrderBreakdown}
					loading={false}
					currency={paymentCurrency}
				/>
			</Box>
		</Box>
	);
};
